/**
 * RH Dashboard — Orchestrator
 * Sub-components extracted to /pages/rh/ for maintainability.
 */
import React from 'react';
import { Routes, Route, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Toaster } from '../components/ui/sonner';
import { NotificationBell } from '../components/NotificationBell';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger
} from '../components/ui/dropdown-menu';
import {
  Activity, Users, BarChart3, LogOut, Home, Building2, Link2, Mail, MessageCircle
} from 'lucide-react';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from '../i18n';

// Sub-components
import DashboardOverview from './rh/DashboardOverview';
import StudentsList from './rh/StudentsList';
import SectorRanking from './rh/SectorRanking';
import IntegrationSettings from './rh/IntegrationSettings';
import MessageHistory from './rh/MessageHistory';
import RHContactPage from './rh/RHContactPage';
import CompanyProfilePage from './rh/CompanyProfilePage';

const RHDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const navItems = [
    { path: '/rh', label: t('rhNav.dashboard'), icon: <Home className="w-5 h-5" /> },
    { path: '/rh/ranking', label: t('rhNav.ranking'), icon: <BarChart3 className="w-5 h-5" /> },
    { path: '/rh/students', label: t('rhNav.students'), icon: <Users className="w-5 h-5" /> },
    { path: '/rh/messages', label: t('rhNav.messages'), icon: <MessageCircle className="w-5 h-5" /> },
    { path: '/rh/profile', label: t('rhNav.profile'), icon: <Building2 className="w-5 h-5" /> },
    { path: '/rh/integrations', label: t('rhNav.integrations'), icon: <Link2 className="w-5 h-5" /> },
    { path: '/rh/contact', label: t('rhNav.contact'), icon: <Mail className="w-5 h-5" /> }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Toaster position="top-center" />
      
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-200 z-40 hidden lg:block safe-top">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-8">
            <Activity className="w-8 h-8 text-blue-600" />
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              Vital
            </span>
          </div>
          
          <nav className="space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/rh'}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-slate-900 text-white'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`
                }
              >
                {item.icon}
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name}</p>
              <p className="text-xs text-slate-500">{t('rh.roleLabel')}</p>
            </div>
          </div>
          <div className="mb-3">
            <LanguageSwitcher />
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            {t('common.logout')}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden glass fixed top-0 w-full z-50 border-b border-slate-200/50 safe-top">
        <div className="px-4 h-14 flex items-center justify-between safe-x">
          <div className="flex items-center gap-2">
            <Activity className="w-6 h-6 text-blue-600" />
            <span className="font-bold text-slate-900">Vital</span>
          </div>
          <div className="flex items-center gap-1">
            <LanguageSwitcher />
            <NotificationBell />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-sm">
                    {user?.name?.charAt(0).toUpperCase()}
                  </span>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={handleLogout}>
                <LogOut className="w-4 h-4 mr-2" />
                {t('common.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pb-20 lg:pb-0" style={{ paddingTop: 'calc(3.5rem + env(safe-area-inset-top, 0px))' }}>
        <div className="p-4 sm:p-6 lg:p-8 lg:pt-8">
          <div className="lg:pt-0">
          <Routes>
            <Route index element={<DashboardOverview />} />
            <Route path="ranking" element={<SectorRanking />} />
            <Route path="students" element={<StudentsList />} />
            <Route path="messages" element={<MessageHistory />} />
            <Route path="profile" element={<CompanyProfilePage />} />
            <Route path="integrations" element={<IntegrationSettings />} />
            <Route path="contact" element={<RHContactPage />} />
          </Routes>
          </div>
        </div>
      </main>

      {/* Mobile Bottom Nav */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-40 safe-bottom safe-x">
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.path === '/rh'}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-1 py-2 min-w-0 ${
                  isActive ? 'text-blue-600' : 'text-slate-500'
                }`
              }
            >
              {item.icon}
              <span className="text-[10px] leading-tight truncate max-w-[56px] text-center">{item.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default RHDashboard;
